import React, { useEffect, useRef, useState } from 'react';

/*
 * Hero Accumulation — "proof accumulates in space"
 *
 * Behind-hero animation that plays once on viewport enter. An abstract world
 * map fades in at low opacity; pins drop onto it in a staggered sequence
 * clustered around real geographies; one pin (Manchester) pulses after the
 * sequence settles and flashes a "Manchester" tooltip beside it.
 *
 * Desktop: full world view with 32 pins across UK, N.Europe, US East Coast,
 * Gulf, SE Asia, Oceania — arguing "territory accumulates globally."
 *
 * Mobile: zooms into UK + N.Europe (viewBox 380 80 200 180) and renders only
 * the 17 UK/Europe pins. At mobile screen sizes the contractor's *regional*
 * density reads more powerfully than a global scatter of unreadable dots,
 * AND it clears the hero headline which otherwise covers the Europe cluster.
 *
 * One inline SVG holds both the base map (as <image>) and the pin layer so
 * a single viewBox swap zooms both in lockstep.
 *
 * Respects prefers-reduced-motion by showing the settled state with no
 * timing animation at all.
 */

interface Pin {
  lat: number;
  lng: number;
  delay: number;
}

// Real cities across deliberate clusters. First 17 are UK + Europe (mobile
// subset); remainder are global and desktop-only.
const PINS: Pin[] = [
  // UK + Ireland (home base — densest). Indices 0–8.
  { lat: 51.5074, lng: -0.1278, delay: 200 },   // London
  { lat: 52.4862, lng: -1.8904, delay: 350 },   // Birmingham
  { lat: 53.4808, lng: -2.2426, delay: 500 },   // Manchester (HERO PIN)
  { lat: 55.9533, lng: -3.1883, delay: 650 },   // Edinburgh
  { lat: 51.4816, lng: -3.1791, delay: 800 },   // Cardiff
  { lat: 51.4545, lng: -2.5879, delay: 950 },   // Bristol
  { lat: 55.8642, lng: -4.2518, delay: 1100 },  // Glasgow
  { lat: 53.8008, lng: -1.5491, delay: 1250 },  // Leeds
  { lat: 53.3498, lng: -6.2603, delay: 1400 },  // Dublin
  // Northern + Western Europe. Indices 9–16.
  { lat: 52.3676, lng:  4.9041, delay: 1050 },  // Amsterdam
  { lat: 50.8503, lng:  4.3517, delay: 1200 },  // Brussels
  { lat: 48.8566, lng:  2.3522, delay: 1350 },  // Paris
  { lat: 52.5200, lng: 13.4050, delay: 1500 },  // Berlin
  { lat: 53.5511, lng: 10.0000, delay: 1650 },  // Hamburg
  { lat: 55.6761, lng: 12.5683, delay: 1800 },  // Copenhagen
  { lat: 59.3293, lng: 18.0686, delay: 1950 },  // Stockholm
  { lat: 59.9139, lng: 10.7522, delay: 2100 },  // Oslo
  // US East Coast. Indices 17–22. Desktop only.
  { lat: 40.7128, lng: -74.0060, delay:  600 }, // New York
  { lat: 42.3601, lng: -71.0589, delay:  850 }, // Boston
  { lat: 39.9526, lng: -75.1652, delay: 1100 }, // Philadelphia
  { lat: 38.9072, lng: -77.0369, delay: 1350 }, // Washington DC
  { lat: 39.2904, lng: -76.6122, delay: 1600 }, // Baltimore
  { lat: 33.7490, lng: -84.3880, delay: 1850 }, // Atlanta
  // Gulf. Indices 23–26. Desktop only.
  { lat: 25.2048, lng: 55.2708, delay: 1500 },  // Dubai
  { lat: 24.4539, lng: 54.3773, delay: 1700 },  // Abu Dhabi
  { lat: 25.2854, lng: 51.5310, delay: 1900 },  // Doha
  { lat: 24.7136, lng: 46.6753, delay: 2100 },  // Riyadh
  // SE Asia. Indices 27–29. Desktop only.
  { lat:  1.3521, lng: 103.8198, delay: 2000 }, // Singapore
  { lat: 22.3193, lng: 114.1694, delay: 2300 }, // Hong Kong
  { lat:  3.1390, lng: 101.6869, delay: 2600 }, // Kuala Lumpur
  // Oceania. Indices 30–31. Desktop only.
  { lat: -33.8688, lng: 151.2093, delay: 2500 }, // Sydney
  { lat: -37.8136, lng: 144.9631, delay: 2700 }, // Melbourne
];

const HERO_PIN_INDEX = 2; // Manchester
const EUROPE_PIN_COUNT = 17; // Indices 0–16

const MAP_W = 950;
const MAP_H = 620;

// viewBox crops for each breakpoint
const VIEWBOX_DESKTOP = `0 0 ${MAP_W} ${MAP_H}`;
const VIEWBOX_MOBILE = '380 80 200 180'; // UK + N. Europe region

// Calibrated empirically against country centroids measured from
// world-map.svg (getBBox on known <path id="...">). The file isn't pure
// equirectangular — Antarctica is cropped, so the equator sits around
// y≈349 rather than y=310, and lng=0 sits around x≈452 rather than x=475.
// Verified fits: britain(447,165), france(458,193), mexico(176,258),
// egypt(536,258), india(679,270), australia(814,435), brazil(301,382).
function latLngToXY(lat: number, lng: number): { x: number; y: number } {
  const x = 2.71 * lng + 452.4;
  const y = -3.34 * lat + 348.7;
  return { x, y };
}

const HeroAccumulation: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track breakpoint so viewBox + pin-subset swap when rotating / resizing.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setStarted(true);
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const heroPin = PINS[HERO_PIN_INDEX];
  const hero = latLngToXY(heroPin.lat, heroPin.lng);

  // On mobile the Europe-focus crop means the rest of the world is off-
  // screen anyway, but filtering also cuts animation work and keeps the
  // staggered cascade from feeling thin.
  const visiblePins = isMobile ? PINS.slice(0, EUROPE_PIN_COUNT) : PINS;
  const viewBox = isMobile ? VIEWBOX_MOBILE : VIEWBOX_DESKTOP;

  return (
    <div
      ref={rootRef}
      className={`hero-accumulation ${started ? 'is-started' : ''} ${isMobile ? 'is-mobile' : ''}`}
      aria-hidden="true"
    >
      <svg
        className="hero-accumulation__svg"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
      >
        <image
          className="hero-accumulation__map"
          href="/images/landing/world-map.svg"
          x="0"
          y="0"
          width={MAP_W}
          height={MAP_H}
        />

        {visiblePins.map((p, i) => {
          const { x, y } = latLngToXY(p.lat, p.lng);
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              className="hero-accumulation__pin"
              style={{ transitionDelay: `${p.delay}ms` }}
            />
          );
        })}

        {/* Pulse ring emanating from the hero pin after the sequence settles.
            r stays fixed at 3 via attribute (Safari iOS doesn't reliably
            animate CSS `r`); the ring expands via transform: scale() instead. */}
        <circle
          cx={hero.x}
          cy={hero.y}
          r="3"
          className="hero-accumulation__pulse"
        />

        {/* Location label — flashes beside the hero pin like a tooltip.
            Closes the animation's argument ("each pin is a real place"). */}
        <g
          className="hero-accumulation__thumbnail"
          transform={`translate(${hero.x + 8}, ${hero.y - 14})`}
        >
          <rect
            width="56"
            height="16"
            rx="3"
            fill="#0f172a"
          />
          <text
            x="28"
            y="11"
            textAnchor="middle"
            fontSize="8"
            fontFamily="Geist, system-ui, sans-serif"
            fontWeight="500"
            fill="#ffffff"
            letterSpacing="-0.01em"
          >
            Manchester
          </text>
        </g>
      </svg>
    </div>
  );
};

export default HeroAccumulation;
